import { Suspense, lazy, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  Broadcast,
  ChartLineUp,
  Check,
  Copy,
  DownloadSimple,
  House,
  Images,
  Moon,
  ShareNetwork,
  Sun,
  UsersThree,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { APP_CONFIG, type ImageKey } from "./config";
import { messages, type Lang, type Messages } from "./i18n";
import { formatInteger, formatTime, formatUsd, useMainstreamTickers, useTokenMarket, type LoadState, type TickerItem, type TokenMarket } from "./lib/market";

const PriceChart = lazy(() => import("./components/PriceChart").then((module) => ({ default: module.PriceChart })));

type Theme = "dark" | "light";
type ToastState = { message: string; tone: "success" | "error" } | null;
type DeferredInstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

const CONTRACT_SHORT = `${APP_CONFIG.token.contract.slice(0, 8)}...${APP_CONFIG.token.contract.slice(-4)}`;

function usePersistedPreference<T extends string>(key: string, fallback: T) {
  const [value, setValue] = useState<T>(() => (window.localStorage.getItem(key) as T | null) ?? fallback);
  const update = (next: T) => {
    setValue(next);
    window.localStorage.setItem(key, next);
  };
  return [value, update] as const;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

function App() {
  const [lang, setLang] = usePersistedPreference<Lang>("ybh-lang", "zh");
  const [theme, setTheme] = usePersistedPreference<Theme>("ybh-theme", "dark");
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [showLoader, setShowLoader] = useState(true);
  const [installPrompt, setInstallPrompt] = useState<DeferredInstallPrompt | null>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const copy = messages[lang] as Messages;
  const market = useTokenMarket();
  const tickers = useMainstreamTickers();
  const reduceMotion = useReducedMotion();
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, reduceMotion ? 1 : 0.965]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.92], [1, reduceMotion ? 1 : 0.28]);
  const finishLoader = useCallback(() => setShowLoader(false), []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang, theme]);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as DeferredInstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const copyContract = async () => {
    try {
      await navigator.clipboard.writeText(APP_CONFIG.token.contract);
      setToast({ message: copy.copied, tone: "success" });
    } catch {
      setToast({ message: copy.copyFailed, tone: "error" });
    }
  };

  const installApp = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <div className="site-shell">
      <AnimatePresence>{showLoader && <FirstLoader copy={copy} marketState={market.state} onDone={finishLoader} />}</AnimatePresence>
      <Header lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} copy={copy} menuOpen={menuOpen} setMenuOpen={setMenuOpen} copyContract={copyContract} />

      {isMobile ? (
        <MobileSite market={market.data} state={market.state} reload={market.reload} tickerResource={tickers} copy={copy} lang={lang} theme={theme} copyContract={copyContract} />
      ) : (
        <>
          <main>
            <motion.section id="home" className="hero" ref={heroRef} style={{ scale: heroScale, opacity: heroOpacity }}>
              <div className="hero-art" aria-hidden="true">
                <OptimizedImage imageKey="hero" alt="" eager />
                <div className="hero-scrim" />
              </div>
              <div className="hero-content page-width">
                <motion.div className="hero-copy glass-panel" initial={reduceMotion ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.18 }}>
                  <p className="hero-eyebrow">{copy.hero.english}</p>
                  <h1>{copy.hero.title}</h1>
                  <p className="hero-statement">
                    <span>{copy.hero.line1}</span>
                    <strong>{copy.hero.line2}</strong>
                    <small>{copy.hero.body}</small>
                  </p>
                  <div className="button-row">
                    <a className="button primary" href="#market"><ChartLineUp />{copy.hero.market}</a>
                    <button className="button secondary" onClick={copyContract}><Copy />{copy.hero.copy}</button>
                  </div>
                </motion.div>
                <HeroMetrics market={market.data} state={market.state} copy={copy} lang={lang} reload={market.reload} />
              </div>
            </motion.section>

            <MarketTicker resource={tickers} market={market.data} copy={copy} />
            <MarketSection market={market.data} state={market.state} reload={market.reload} copy={copy} lang={lang} theme={theme} />
            <TrendSection copy={copy} />
            <FutureSection copy={copy} />
            <YearSection copy={copy} lang={lang} />
            <StorySection copy={copy} />
            <GallerySection copy={copy} />
            <ProfileSection copy={copy} />
            <CommunitySection copy={copy} />
            <ContractSection copy={copy} copyContract={copyContract} installPrompt={installPrompt} installApp={installApp} />
          </main>
          <Footer copy={copy} copyContract={copyContract} />
        </>
      )}
      {isMobile && <MobileDock copy={copy} />}
      <AnimatePresence>
        {toast && (
          <motion.div className={`toast ${toast.tone}`} role="status" initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12 }}>
            {toast.tone === "success" ? <Check weight="bold" /> : <WarningCircle />}<span>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileSite({ market, state, reload, tickerResource, copy, lang, theme, copyContract }: {
  market: TokenMarket | null;
  state: LoadState;
  reload: () => void;
  tickerResource: ReturnType<typeof useMainstreamTickers>;
  copy: Messages;
  lang: Lang;
  theme: Theme;
  copyContract: () => void;
}) {
  const remaining = getCountdown();
  const marketReady = state === "success" && market;
  const quickLinks = [
    [copy.nav.market, copy.metric.price, "#market", Broadcast],
    [copy.nav.chart, copy.marketBody, "#chart", ChartLineUp],
    [copy.nav.memes, copy.gallery.body, "#memes", Images],
    [copy.nav.community, copy.community.body, "#community", UsersThree],
  ] as const;
  const overviewStats = [
    [copy.metric.volume, market?.volume24h, "usd"],
    [copy.metric.marketCap, market?.marketCap, "usd"],
    [copy.metric.liquidity, market?.liquidityUsd, "usd"],
  ] as const;
  const marketStats = [
    [copy.metric.high, market?.high24h, "usd"],
    [copy.metric.low, market?.low24h, "usd"],
    [copy.metric.trades, market?.trades24h, "integer"],
    [copy.metric.buys, market?.buys24h, "integer"],
    [copy.metric.sells, market?.sells24h, "integer"],
    [copy.metric.fdv, market?.fdv, "usd"],
  ] as const;
  const communityLinks = [
    [copy.community.x, APP_CONFIG.socials.x],
    [copy.community.telegram, APP_CONFIG.socials.telegram],
    [copy.community.explorer, APP_CONFIG.token.explorer],
    ["DexScreener", APP_CONFIG.token.dexScreener],
  ] as const;
  const targetTicker: TickerItem | null = market?.priceUsd === null || market?.priceUsd === undefined ? null : {
    symbol: APP_CONFIG.token.symbol,
    pair: `${APP_CONFIG.token.symbol}USDT`,
    price: market.priceUsd,
    change24h: market.change24h ?? 0,
  };
  const tickerItems = [...(tickerResource.data ?? []), ...(targetTicker ? [targetTicker] : [])].slice(0, 9);
  const tickerTrack = [...tickerItems, ...tickerItems];
  const plan = lang === "zh" ? {
    intro: "一步一步，把一个梗做成长期 IP",
    title: "羊必火发展规划",
    note: "规划会根据社区反馈和实际资源调整，具体安排以官方公告为准。",
    items: [
      ["当前", "先把地基搭稳", "持续完善官网、实时行情、Meme 图鉴与合约信息。"],
      ["下一步", "让羊群一起造梗", "开放主题征集、表情包共创和社区内容投票。"],
      ["2027", "把羊年做成主场", "围绕羊年策划连载内容、线上活动与联名合作。"],
      ["长期", "从一个梗走向世界观", "探索角色矩阵、IP 授权和可持续内容栏目。"],
    ],
  } : {
    intro: "Step by step, turn one meme into a lasting IP",
    title: "Yang Bi Huo Roadmap",
    note: "The roadmap may change with community feedback and available resources. Official announcements take priority.",
    items: [
      ["NOW", "Build the foundation", "Keep improving the site, live market data, meme gallery and contract references."],
      ["NEXT", "Create with the flock", "Open themed submissions, meme collaborations and community content votes."],
      ["2027", "Make the Year of the Goat ours", "Plan serial content, online events and selected collaborations."],
      ["LONG TERM", "Grow a complete world", "Explore a character roster, IP licensing and sustainable content formats."],
    ],
  };
  const memeCulture: {
    intro: string;
    title: string;
    featured: { image: ImageKey; title: string; body: string };
    cards: Array<{ image: ImageKey; title: string; body: string }>;
    statement: string;
  } = lang === "zh" ? {
    intro: "每一张图，都是羊群接梗的起点",
    title: "羊系 Meme 文化",
    featured: {
      image: "trend",
      title: "羊总宇宙，今日营业",
      body: "海滩、K 线、月球、派对和羊圈会议。同一只羊，随时切换身份。",
    },
    cards: [
      { image: "market", title: "今晚羊了个羊", body: "开盘看 K 线，收盘开派对。" },
      { image: "story", title: "赛博羊城", body: "霓虹亮了，羊总才刚出门。" },
      { image: "culture", title: "月球打卡", body: "不是登月，是去看看下一站。" },
      { image: "community", title: "羊总朋友圈", body: "阵容不重要，气氛必须到位。" },
      { image: "ending", title: "羊圈开会", body: "今天只讨论一件事：谁来造梗？" },
      { image: "avatar", title: "今天也有表情", body: "不说话，也能让全群懂你。" },
    ],
    statement: "真正的 Meme 不是看完就走，而是每个人都能改一版、接一句、继续讲下去。",
  } : {
    intro: "Every image is an invitation to remix",
    title: "The Goat Meme Culture",
    featured: {
      image: "trend",
      title: "The goat universe is open",
      body: "Beach, charts, moon missions, parties and flock meetings. One goat, endless roles.",
    },
    cards: [
      { image: "market", title: "Goat the night away", body: "Charts by day, decks by night." },
      { image: "story", title: "Cyber Goat City", body: "The neon turns on when the goat steps out." },
      { image: "culture", title: "Moon check-in", body: "Not a landing, just scouting the next stop." },
      { image: "community", title: "The goat circle", body: "The guest list changes. The energy does not." },
      { image: "ending", title: "Flock meeting", body: "One agenda item: who makes the next meme?" },
      { image: "avatar", title: "Mood of the day", body: "No caption needed. The whole flock gets it." },
    ],
    statement: "A real meme is not something you only view. It is something everyone can remix, caption and continue.",
  };

  return (
    <main className="mobile-site">
      <section id="home" className="mobile-dashboard page-width">
        <figure className="mobile-overview-hero mobile-panel">
          <OptimizedImage imageKey="market" alt="羊必火 DJ 与 Meme 社区伙伴在派对现场" eager />
        </figure>

        <article className="mobile-overview mobile-panel">
          <div className="mobile-overview-head">
            <span>{APP_CONFIG.token.symbol} {copy.metric.price}</span>
            <small className={state === "success" ? "synced" : ""}>{state === "success" ? copy.updated : copy.pending}</small>
          </div>
          <div className="mobile-price-row">
            <strong>{marketReady ? formatUsd(market.priceUsd) : copy.pending}</strong>
            <span className={(market?.change24h ?? 0) >= 0 ? "up" : "down"}>{marketReady && market.change24h !== null ? `${market.change24h >= 0 ? "+" : ""}${market.change24h.toFixed(2)}%` : copy.unavailable}</span>
          </div>
          <div className="mobile-stat-strip">
            {overviewStats.map(([label, value, format]) => (
              <div key={label}><span>{label}</span><strong>{formatMarketValue(value ?? null, format, copy)}</strong></div>
            ))}
          </div>
          <nav className="mobile-action-grid" aria-label="Mobile shortcuts">
            {quickLinks.map(([label, note, href, Icon]) => (
              <a href={href} key={href}><Icon /><span><strong>{label}</strong><small>{note}</small></span><ArrowRight /></a>
            ))}
          </nav>
        </article>

        <section className="mobile-live-ticker mobile-panel" aria-label={lang === "zh" ? "主流虚拟货币实时行情" : "Live crypto market ticker"}>
          {tickerTrack.length ? (
            <div className="mobile-live-ticker-track">
              {tickerTrack.map((item, index) => (
                <div className="mobile-live-ticker-cell" key={`${item.pair}-${index}`}>
                  <strong>{item.symbol}</strong>
                  <span>{formatUsd(item.price, false)}</span>
                  <b className={item.change24h >= 0 ? "up" : "down"}>{item.change24h >= 0 ? "+" : ""}{item.change24h.toFixed(2)}%</b>
                </div>
              ))}
            </div>
          ) : (
            <div className="mobile-live-ticker-empty"><Broadcast /><span>{tickerResource.state === "error" ? copy.apiError : copy.loadingMarket}</span>{tickerResource.state === "error" && <button onClick={tickerResource.reload}>{copy.retry}</button>}</div>
          )}
        </section>

        <a id="year-2027" className="mobile-year-banner mobile-panel" href="#year-card">
          <span className="mobile-year-thumb"><OptimizedImage imageKey="year2027" alt="2027羊年" /></span>
          <span><small>{copy.year.title}</small><strong>{copy.year.slogans[0]}</strong><em>{copy.year.extra}</em></span>
          <b>{remaining.days}<small>{copy.year.units[0]}</small></b>
          <ArrowRight />
        </a>

        <article id="year-card" className="mobile-visual-card mobile-panel">
          <div className="mobile-visual-art"><OptimizedImage imageKey="hero" alt="羊必火海底寻宝场景" eager /></div>
          <div className="mobile-visual-copy"><span>{copy.hero.english}</span><h1>{copy.hero.title}</h1><p>{copy.hero.line2}</p></div>
        </article>

        <section id="market" className="mobile-market mobile-panel">
          <div className="mobile-card-head"><div><small>{copy.updated}: {formatTime(market?.updatedAt ?? null, lang) || copy.pending}</small><h2>{copy.marketTitle}</h2></div><a href={APP_CONFIG.token.dexScreener} target="_blank" rel="noreferrer">DexScreener <ArrowSquareOut /></a></div>
          {state === "error" || state === "offline" ? (
            <div className="mobile-inline-error"><WarningCircle /><span>{state === "offline" ? copy.offline : copy.apiError}</span><button onClick={reload}>{copy.retry}</button></div>
          ) : (
            <div className="mobile-market-grid">
              {marketStats.map(([label, value, format]) => <div key={label}><span>{label}</span><strong>{formatMarketValue(value ?? null, format, copy)}</strong></div>)}
            </div>
          )}
          <div id="chart" className="mobile-chart">
            <Suspense fallback={<div className="chart-shell chart-loading-shell"><span>{copy.loadingChart}</span></div>}>
              <PriceChart lang={lang} copy={copy} theme={theme} />
            </Suspense>
          </div>
        </section>

        <section className="mobile-roadmap mobile-panel">
          <div className="mobile-card-head"><div><small>{plan.intro}</small><h2>{plan.title}</h2></div></div>
          <div className="mobile-plan-grid">
            {plan.items.map(([phase, title, body]) => <article key={phase}><span>{phase}</span><h3>{title}</h3><p>{body}</p></article>)}
          </div>
          <p className="mobile-plan-note">{plan.note}</p>
        </section>

        <section id="memes" className="mobile-meme-culture mobile-panel">
          <div className="mobile-card-head"><div><small>{memeCulture.intro}</small><h2>{memeCulture.title}</h2></div></div>
          <article className="mobile-meme-featured">
            <OptimizedImage imageKey={memeCulture.featured.image} alt={memeCulture.featured.title} />
            <div><h3>{memeCulture.featured.title}</h3><p>{memeCulture.featured.body}</p></div>
          </article>
          <div className="mobile-meme-rail">
            {memeCulture.cards.map((card) => (
              <figure key={card.title}>
                <OptimizedImage imageKey={card.image} alt={card.title} />
                <figcaption><strong>{card.title}</strong><span>{card.body}</span></figcaption>
              </figure>
            ))}
          </div>
          <p className="mobile-meme-statement">{memeCulture.statement}</p>
        </section>

        <section className="mobile-profile mobile-panel">
          <div className="mobile-profile-art"><OptimizedImage imageKey="avatar" alt="羊必火唯一官方头像" /></div>
          <div className="mobile-profile-copy"><small>{copy.profile.title}</small><h2>{copy.story.title}</h2><p>{copy.profile.quotes[0]}</p></div>
          <div className="mobile-profile-facts">{copy.profile.fields.slice(0, 4).map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
        </section>

        <section id="community" className="mobile-community mobile-panel">
          <div className="mobile-card-head"><div><small>{copy.community.body}</small><h2>{copy.community.title}</h2></div></div>
          <div className="mobile-community-list">{copy.community.directions.map(([title, body]) => <article key={title}><span><strong>{title}</strong><small>{body}</small></span><ArrowRight /></article>)}</div>
          <div className="mobile-social-grid">{communityLinks.map(([label, href]) => href ? <a key={label} href={href} target="_blank" rel="noreferrer">{label}<ArrowSquareOut /></a> : <span key={label}>{label}<small>{copy.soon}</small></span>)}</div>
        </section>

        <section className="mobile-contract mobile-panel">
          <div className="mobile-contract-brand"><img src={APP_CONFIG.images.avatar.path} alt="羊必火官方头像" /><span><small>{APP_CONFIG.token.chain}</small><strong>{APP_CONFIG.brand.name}</strong></span></div>
          <code>{APP_CONFIG.token.contract}</code>
          <div className="mobile-contract-actions"><button onClick={copyContract}><Copy />{copy.contractSection.copy}</button><a href={APP_CONFIG.token.explorer} target="_blank" rel="noreferrer"><ArrowSquareOut />{copy.contractSection.explorer}</a></div>
          <p>{copy.risk}</p>
        </section>

        <footer className="mobile-footer"><span>© {new Date().getFullYear()} {copy.footer.copyright}</span><span>{copy.footer.sources}</span></footer>
      </section>
    </main>
  );
}

function FirstLoader({ copy, marketState, onDone }: { copy: Messages; marketState: LoadState; onDone: () => void }) {
  const [progress, setProgress] = useState(0);
  const marketReady = useRef(marketState !== "idle" && marketState !== "loading");
  const visited = window.sessionStorage.getItem("ybh-visited") === "1";
  const duration = visited ? 850 : 2100;
  const text = copy.loader[new Date().getSeconds() % copy.loader.length];

  useEffect(() => {
    marketReady.current = marketState !== "idle" && marketState !== "loading";
  }, [marketState]);

  useEffect(() => {
    const started = performance.now();
    let timer = 0;
    const tick = () => {
      const elapsed = performance.now() - started;
      const waitingForData = !marketReady.current;
      const cap = waitingForData && elapsed < 2900 ? 92 : 100;
      const next = Math.min(cap, Math.round((elapsed / duration) * 100));
      setProgress(next);
      if (next >= 100) {
        window.sessionStorage.setItem("ybh-visited", "1");
        window.setTimeout(onDone, 220);
        return;
      }
      timer = window.setTimeout(tick, 32);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [duration, onDone]);

  return (
    <motion.div className="loader" exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }}>
      <motion.img src={APP_CONFIG.images.avatar.path} alt="羊必火" initial={{ opacity: 0, filter: "blur(12px)", scale: 0.94 }} animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }} transition={{ type: "spring", stiffness: 120, damping: 18 }} />
      <div className="loader-wordmark" aria-label="羊必火"><span>羊</span><span>必</span><span>火</span></div>
      <p>{text}</p>
      <div className="loader-track"><motion.span animate={{ scaleX: progress / 100 }} /></div>
      <small>{progress}%</small>
    </motion.div>
  );
}

function Header({ lang, setLang, theme, setTheme, copy, menuOpen, setMenuOpen, copyContract }: {
  lang: Lang;
  setLang: (lang: Lang) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  copy: Messages;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  copyContract: () => void;
}) {
  const nav = [[copy.nav.home, "#home"], [copy.nav.market, "#market"], [copy.nav.chart, "#chart"], [copy.nav.memes, "#memes"], [copy.nav.year, "#year-2027"], [copy.nav.community, "#community"]];
  return (
    <>
      <header className="topbar glass-panel">
        <a href="#home" className="brand-lockup" aria-label="羊必火">
          <img src={APP_CONFIG.images.avatar.path} alt="羊必火官方头像" />
          <span><strong>羊必火</strong><small>YANG BI HUO</small></span>
        </a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {nav.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
        </nav>
        <div className="nav-actions">
          <button className="contract-mini" onClick={copyContract}><Copy />{CONTRACT_SHORT}</button>
          <button className="icon-control" onClick={() => setLang(lang === "zh" ? "en" : "zh")} aria-label="Language">{lang === "zh" ? "EN" : "中"}</button>
          <button className="icon-control" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={theme === "dark" ? "Light theme" : "Dark theme"}>{theme === "dark" ? <Sun /> : <Moon />}</button>
          <a className="icon-control desktop-community" href="#community" aria-label={copy.nav.community}><UsersThree /></a>
          <button className="menu-trigger icon-control" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? copy.nav.close : copy.nav.menu}>{menuOpen ? <X /> : <span className="menu-lines" />}</button>
        </div>
      </header>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav className="mobile-drawer glass-panel" initial={{ opacity: 0, y: -14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.98 }}>
            {nav.map(([label, href]) => <a href={href} key={href} onClick={() => setMenuOpen(false)}>{label}<ArrowRight /></a>)}
            <button onClick={() => { copyContract(); setMenuOpen(false); }}><Copy />{copy.nav.copy}</button>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}

function HeroMetrics({ market, state, copy, lang, reload }: { market: TokenMarket | null; state: LoadState; copy: Messages; lang: Lang; reload: () => void }) {
  const values = [[copy.metric.price, market?.priceUsd ?? null, true], [copy.metric.change, market?.change24h ?? null, false], [copy.metric.volume, market?.volume24h ?? null, true], [copy.metric.marketCap, market?.marketCap ?? null, true]] as const;
  return (
    <div className="hero-metrics glass-panel" aria-live="polite">
      {values.map(([label, value, money], index) => (
        <div className="hero-metric" key={label}>
          <span>{label}</span>
          {state === "loading" || state === "idle" ? <i className="value-skeleton" /> : value === null ? <strong className="muted">{copy.pending}</strong> : <AnimatedValue value={money ? formatUsd(value) : `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`} tone={index === 1 ? (value >= 0 ? "up" : "down") : undefined} />}
        </div>
      ))}
      <div className="hero-update">
        {state === "error" || state === "offline" ? <button onClick={reload}>{copy.retry}</button> : <span>{copy.updated}: {formatTime(market?.updatedAt ?? null, lang) || copy.pending}</span>}
      </div>
    </div>
  );
}

function AnimatedValue({ value, tone }: { value: string; tone?: "up" | "down" }) {
  return <motion.strong key={value} className={tone} initial={{ opacity: 0.45, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>{value}</motion.strong>;
}

function MarketTicker({ resource, market, copy }: { resource: ReturnType<typeof useMainstreamTickers>; market: TokenMarket | null; copy: Messages }) {
  const target: TickerItem | null = market?.priceUsd === null || market?.priceUsd === undefined ? null : { symbol: APP_CONFIG.token.symbol, pair: `${APP_CONFIG.token.symbol}USDT`, price: market.priceUsd, change24h: market.change24h ?? 0 };
  const items = [...(resource.data ?? []), ...(target ? [target] : [])];
  const track = items.length ? [...items, ...items] : [];
  return (
    <section className="ticker-section" aria-label={copy.nav.market}>
      {track.length ? (
        <div className="ticker-track">
          {track.map((item, index) => <TickerCell key={`${item.pair}-${index}`} item={item} />)}
        </div>
      ) : (
        <div className="ticker-loading"><Broadcast /><span>{resource.state === "error" ? copy.apiError : copy.loadingMarket}</span>{resource.state === "error" && <button onClick={resource.reload}>{copy.retry}</button>}</div>
      )}
    </section>
  );
}

function TickerCell({ item }: { item: TickerItem }) {
  const up = item.change24h >= 0;
  const y1 = up ? 16 : 5;
  const y2 = up ? 5 : 16;
  return (
    <div className="ticker-cell">
      <span className="coin-mark">{item.symbol.slice(0, 2)}</span>
      <span><strong>{item.symbol}</strong><small>{item.pair.replace("USDT", "/USDT")}</small></span>
      <b>{formatUsd(item.price, false)}</b>
      <span className={up ? "change up" : "change down"}>{up ? "+" : ""}{item.change24h.toFixed(2)}%</span>
      <svg className={up ? "spark up" : "spark down"} viewBox="0 0 42 22" aria-hidden="true"><polyline points={`2,${y1} 12,${(y1 + y2) / 2 + 2} 22,${(y1 + y2) / 2 - 2} 40,${y2}`} /></svg>
    </div>
  );
}

function MarketSection({ market, state, reload, copy, lang, theme }: { market: TokenMarket | null; state: LoadState; reload: () => void; copy: Messages; lang: Lang; theme: Theme }) {
  const values = [
    [copy.metric.price, market?.priceUsd, "usd"],
    [copy.metric.change, market?.change24h, "percent"],
    [copy.metric.high, market?.high24h, "usd"],
    [copy.metric.low, market?.low24h, "usd"],
    [copy.metric.volume, market?.volume24h, "usd"],
    [copy.metric.liquidity, market?.liquidityUsd, "usd"],
    [copy.metric.marketCap, market?.marketCap, "usd"],
    [copy.metric.fdv, market?.fdv, "usd"],
    [copy.metric.trades, market?.trades24h, "integer"],
    [copy.metric.buys, market?.buys24h, "integer"],
    [copy.metric.sells, market?.sells24h, "integer"],
    [copy.metric.holders, null, "text"],
  ] as const;
  const change = market?.change24h;
  const mood = change === null || change === undefined ? copy.mood.flat : Math.abs(change) >= 15 ? copy.mood.volatile : change > 1 ? copy.mood.up : change < -1 ? copy.mood.down : copy.mood.flat;
  return (
    <section id="market" className="market-section section-space image-section">
      <div className="section-image-fusion"><OptimizedImage imageKey="market" alt="羊必火在Meme派对中担任DJ" /></div>
      <div className="page-width market-layout">
        <Reveal className="market-copy">
          <h2>{copy.marketTitle}</h2>
          <p>{copy.marketBody}</p>
          <GlassPanel className="mood-card">
            <span>{mood}</span>
            <small>{copy.memeDisclaimer}</small>
          </GlassPanel>
        </Reveal>
        <GlassPanel className="market-panel">
          <div className="market-panel-head">
            <div><h3>{APP_CONFIG.token.symbol}</h3><p>{APP_CONFIG.token.pairName} / {APP_CONFIG.token.dex}</p></div>
            <span className="verified-source">{state === "success" ? "LIVE" : copy.pending}</span>
          </div>
          {state === "error" || state === "offline" ? (
            <div className="inline-error"><WarningCircle /><span>{state === "offline" ? copy.offline : copy.apiError}</span><button className="button secondary compact" onClick={reload}>{copy.retry}</button></div>
          ) : (
            <div className="market-metrics">
              {values.map(([label, value, format]) => (
                <div className="market-row" key={label}>
                  <span>{label}</span>
                  {state === "loading" || state === "idle" || state === "retrying" ? <i className="value-skeleton" /> : <strong className={format === "percent" && typeof value === "number" ? (value >= 0 ? "up" : "down") : undefined}>{formatMarketValue(value ?? null, format, copy)}</strong>}
                </div>
              ))}
            </div>
          )}
          <div className="market-meta">
            <span>{copy.metric.chain}: {APP_CONFIG.token.chain}</span>
            <a href={APP_CONFIG.token.dexScreener} target="_blank" rel="noreferrer">DexScreener <ArrowSquareOut /></a>
            <span>{copy.updated}: {formatTime(market?.updatedAt ?? null, lang) || copy.pending}</span>
          </div>
        </GlassPanel>
        <div id="chart" className="chart-column">
          <Suspense fallback={<div className="chart-shell chart-loading-shell"><span>{copy.loadingChart}</span></div>}>
            <PriceChart lang={lang} copy={copy} theme={theme} />
          </Suspense>
        </div>
      </div>
      <RiskNotice copy={copy} />
    </section>
  );
}

function formatMarketValue(value: number | null, format: "usd" | "percent" | "integer" | "text", copy: Messages) {
  if (value === null || !Number.isFinite(value)) return copy.unavailable;
  if (format === "usd") return formatUsd(value);
  if (format === "percent") return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  if (format === "integer") return formatInteger(value);
  return copy.unavailable;
}

function TrendSection({ copy }: { copy: Messages }) {
  return (
    <section className="trend-section section-space image-section">
      <div className="section-image-fusion trend-image"><OptimizedImage imageKey="trend" alt="羊必火在不同Meme世界中的场景合集" /></div>
      <div className="page-width">
        <Reveal className="section-heading narrow"><h2>{copy.trend.title}</h2><p>{copy.trend.intro}</p></Reveal>
        <div className="trend-grid">
          {copy.trend.cards.map(([title, body], index) => (
            <Reveal key={title} delay={index * 0.05} className={`trend-card trend-card-${index + 1}`}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{body}</p></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FutureSection({ copy }: { copy: Messages }) {
  return (
    <section className="future-section section-space">
      <div className="future-visual"><OptimizedImage imageKey="culture" alt="羊必火在月球基地展示羊IP世界" /></div>
      <div className="page-width future-content">
        <Reveal className="section-heading narrow"><h2>{copy.future.title}</h2><p>{copy.future.note}</p></Reveal>
        <div className="future-track">
          {copy.future.items.map(([title, body], index) => (
            <article className={index === 0 ? "future-item featured" : "future-item"} key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function YearSection({ copy, lang }: { copy: Messages; lang: Lang }) {
  const [remaining, setRemaining] = useState(() => getCountdown());
  const [slogan, setSlogan] = useState(0);
  useEffect(() => {
    const countdownTimer = window.setInterval(() => setRemaining(getCountdown()), 1000);
    const sloganTimer = window.setInterval(() => setSlogan((value) => (value + 1) % copy.year.slogans.length), 4200);
    return () => { window.clearInterval(countdownTimer); window.clearInterval(sloganTimer); };
  }, [copy.year.slogans.length]);
  return (
    <section id="year-2027" className="year-section section-space image-section">
      <div className="year-background"><OptimizedImage imageKey="year2027" alt="羊必火在东方未来山海世界迎接2027羊年" /></div>
      <div className="page-width year-content">
        <Reveal className="year-copy glass-panel"><h2>{copy.year.title}</h2><p>{copy.year.body}</p><small>{copy.year.extra}</small></Reveal>
        <GlassPanel className="countdown-card">
          <div className="countdown-grid">
            {[remaining.days, remaining.hours, remaining.minutes, remaining.seconds].map((value, index) => (
              <div key={copy.year.units[index]}><AnimatePresence mode="popLayout"><motion.strong key={value} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>{String(value).padStart(2, "0")}</motion.strong></AnimatePresence><span>{copy.year.units[index]}</span></div>
            ))}
          </div>
          <div className="slogan-rotator" aria-live="polite"><AnimatePresence mode="wait"><motion.p key={slogan} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>{copy.year.slogans[slogan]}</motion.p></AnimatePresence></div>
          <small>{lang === "zh" ? "目标时区：Asia/Shanghai" : "Target timezone: Asia/Shanghai"}</small>
        </GlassPanel>
      </div>
    </section>
  );
}

function getCountdown() {
  const distance = Math.max(0, new Date(APP_CONFIG.countdownTarget).getTime() - Date.now());
  return { days: Math.floor(distance / 86_400_000), hours: Math.floor((distance / 3_600_000) % 24), minutes: Math.floor((distance / 60_000) % 60), seconds: Math.floor((distance / 1000) % 60) };
}

function StorySection({ copy }: { copy: Messages }) {
  return (
    <section className="story-section section-space">
      <div className="page-width story-frame">
        <Reveal className="story-art"><OptimizedImage imageKey="story" alt="羊必火走在未来城市中展示老板羊性格" /></Reveal>
        <Reveal className="story-copy glass-panel" delay={0.08}><h2>{copy.story.title}</h2><p>{copy.story.body}</p><blockquote>“{copy.story.quote}”</blockquote></Reveal>
      </div>
    </section>
  );
}

const galleryKeys: ImageKey[] = ["story", "culture", "community", "ending", "meme09", "meme10"];

function GallerySection({ copy }: { copy: Messages }) {
  const [active, setActive] = useState<number | null>(null);
  const pointerStart = useRef(0);
  const move = (direction: -1 | 1) => setActive((current) => current === null ? 0 : (current + direction + galleryKeys.length) % galleryKeys.length);
  useEffect(() => {
    if (active === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActive(null);
      if (event.key === "ArrowLeft") move(-1);
      if (event.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="memes" className="gallery-section section-space">
      <div className="page-width">
        <Reveal className="section-heading narrow"><h2>{copy.gallery.title}</h2><p>{copy.gallery.body}</p></Reveal>
        <div className="masonry-grid">
          {galleryKeys.map((key, index) => (
            <Reveal className={`meme-card meme-card-${index + 1}`} key={`${key}-${index}`} delay={index * 0.04}>
              <button onClick={() => setActive(index)} aria-label={`${copy.gallery.open}: ${copy.gallery.captions[index]}`}>
                <OptimizedImage imageKey={key} alt={copy.gallery.captions[index]} />
                <span>{copy.gallery.captions[index]}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {active !== null && (
          <motion.div className="lightbox" role="dialog" aria-modal="true" aria-label={copy.gallery.open} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onPointerDown={(event) => { pointerStart.current = event.clientX; }} onPointerUp={(event) => { const distance = event.clientX - pointerStart.current; if (Math.abs(distance) > 50) move(distance > 0 ? -1 : 1); }}>
            <button className="lightbox-close icon-control" onClick={() => setActive(null)} aria-label={copy.gallery.close}><X /></button>
            <button className="lightbox-nav prev icon-control" onClick={() => move(-1)} aria-label={copy.gallery.prev}><ArrowLeft /></button>
            <motion.figure key={active} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}>
              <img src={APP_CONFIG.images[galleryKeys[active]].original} alt={copy.gallery.captions[active]} />
              <figcaption>{copy.gallery.captions[active]}</figcaption>
            </motion.figure>
            <button className="lightbox-nav next icon-control" onClick={() => move(1)} aria-label={copy.gallery.next}><ArrowRight /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ProfileSection({ copy }: { copy: Messages }) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  return (
    <section className="profile-section section-space">
      <div className="page-width profile-layout">
        <Reveal className="profile-portrait">
          <button onClick={() => setQuoteIndex((value) => (value + 1) % copy.profile.quotes.length)} aria-label={copy.profile.click}>
            <OptimizedImage imageKey="avatar" alt="羊必火唯一官方头像" />
          </button>
          <AnimatePresence mode="wait"><motion.p className="profile-quote glass-panel" key={quoteIndex} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>“{copy.profile.quotes[quoteIndex]}”</motion.p></AnimatePresence>
          <small>{copy.profile.click}</small>
        </Reveal>
        <Reveal className="profile-copy" delay={0.06}>
          <h2>{copy.profile.title}</h2>
          <div className="profile-fields">
            {copy.profile.fields.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CommunitySection({ copy }: { copy: Messages }) {
  const links = [[copy.community.x, APP_CONFIG.socials.x], [copy.community.telegram, APP_CONFIG.socials.telegram], [copy.community.community, APP_CONFIG.socials.community], [copy.community.explorer, APP_CONFIG.token.explorer]] as const;
  return (
    <section id="community" className="community-section section-space image-section">
      <div className="community-art"><OptimizedImage imageKey="community" alt="羊必火与Meme伙伴共同创作社区内容" /></div>
      <div className="page-width community-layout">
        <Reveal className="community-copy glass-panel"><h2>{copy.community.title}</h2><p>{copy.community.body}</p></Reveal>
        <div className="community-directions">
          {copy.community.directions.map(([title, body], index) => <Reveal className={`direction direction-${index + 1}`} key={title} delay={index * 0.05}><span>{index + 1}</span><div><h3>{title}</h3><p>{body}</p></div></Reveal>)}
        </div>
        <div className="social-links">
          {links.map(([label, href]) => href ? <a key={label} href={href} target="_blank" rel="noreferrer">{label}<ArrowSquareOut /></a> : <span key={label}>{label}<small>{copy.soon}</small></span>)}
        </div>
      </div>
    </section>
  );
}

function ContractSection({ copy, copyContract, installPrompt, installApp }: { copy: Messages; copyContract: () => void; installPrompt: DeferredInstallPrompt | null; installApp: () => void }) {
  return (
    <section className="contract-section section-space">
      <div className="page-width contract-layout">
        <Reveal className="contract-copy"><h2>{copy.contractSection.title}</h2><p>{copy.contractSection.body}</p></Reveal>
        <GlassPanel className="contract-card">
          <div className="qr-wrap"><QRCodeSVG value={APP_CONFIG.token.contract} size={156} bgColor="transparent" fgColor="currentColor" level="M" /><small>{copy.contractSection.qr}</small></div>
          <div className="contract-detail">
            <div><img src={APP_CONFIG.images.avatar.path} alt="羊必火官方头像" /><span><strong>{APP_CONFIG.brand.name}</strong><small>{APP_CONFIG.token.chain}</small></span></div>
            <code>{APP_CONFIG.token.contract}</code>
            <p>{APP_CONFIG.token.pairName} / {APP_CONFIG.token.dex}</p>
            <div className="contract-facts">
              <div><span>{copy.metric.symbol}</span><strong>{APP_CONFIG.token.symbol}</strong></div>
              <div><span>{copy.metric.decimals}</span><strong>{APP_CONFIG.token.decimals}</strong></div>
              <div><span>{copy.metric.pool}</span><strong>{`${APP_CONFIG.token.pairAddress.slice(0, 8)}...${APP_CONFIG.token.pairAddress.slice(-4)}`}</strong></div>
            </div>
            <div className="button-row">
              <button className="button primary" onClick={copyContract}><Copy />{copy.contractSection.copy}</button>
              <a className="button secondary" href={APP_CONFIG.token.explorer} target="_blank" rel="noreferrer"><ArrowSquareOut />{copy.contractSection.explorer}</a>
            </div>
            {installPrompt && <button className="install-link" onClick={installApp}><DownloadSimple />{copy.contractSection.install}</button>}
          </div>
        </GlassPanel>
      </div>
      <RiskNotice copy={copy} />
    </section>
  );
}

function RiskNotice({ copy }: { copy: Messages }) {
  return <div className="risk-notice glass-panel page-width"><WarningCircle /><p>{copy.risk}</p></div>;
}

function Footer({ copy, copyContract }: { copy: Messages; copyContract: () => void }) {
  return (
    <footer className="footer image-section">
      <div className="footer-art"><OptimizedImage imageKey="ending" alt="羊必火主持Meme圆桌会议" /></div>
      <div className="page-width footer-content">
        <div className="footer-brand"><img src={APP_CONFIG.images.avatar.path} alt="羊必火官方头像" /><span><strong>羊必火</strong><small>YANG BI HUO</small></span></div>
        <h2>{copy.footer.end}</h2>
        <button className="footer-contract" onClick={copyContract}><code>{APP_CONFIG.token.contract}</code><Copy /></button>
        <div className="footer-links"><a href={APP_CONFIG.token.explorer} target="_blank" rel="noreferrer">BscScan</a><a href={APP_CONFIG.token.dexScreener} target="_blank" rel="noreferrer">DexScreener</a><a href={APP_CONFIG.token.geckoTerminal} target="_blank" rel="noreferrer">GeckoTerminal</a></div>
        <p className="footer-risk">{copy.risk}</p>
        <div className="footer-base"><span>© {new Date().getFullYear()} {copy.footer.copyright}</span><span>{copy.footer.sources}</span></div>
      </div>
    </footer>
  );
}

function MobileDock({ copy }: { copy: Messages }) {
  const links = [[copy.nav.home, "#home", House], [copy.nav.market, "#market", Broadcast], [copy.nav.chart, "#chart", ChartLineUp], [copy.nav.memes, "#memes", Images], [copy.nav.community, "#community", ShareNetwork]] as const;
  const [active, setActive] = useState(() => window.location.hash || "#home");

  useEffect(() => {
    const targets = [...document.querySelectorAll("#home, #market, #chart, #memes, #community, .story-section, .profile-section, .contract-section, .mobile-profile, .mobile-contract, footer")];
    const visibility = new Map<Element, number>();
    const dockTarget = (target: Element) => {
      if (target.id === "chart") return "#chart";
      if (target.id === "home") return "#home";
      if (target.id === "market") return "#market";
      if (target.id === "memes") return "#memes";
      if (target.id === "community") return "#community";
      if (target.matches(".story-section, .profile-section, .mobile-profile")) return "#memes";
      if (target.matches(".contract-section, .mobile-contract, .footer, .mobile-footer")) return "#community";
      return "";
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => visibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
      const visible = [...visibility.entries()].sort((a, b) => b[1] - a[1])[0];
      setActive(visible && visible[1] > 0 ? dockTarget(visible[0]) : "");
    }, { rootMargin: "-18% 0px -62%", threshold: [0, 0.15, 0.4] });
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="mobile-dock glass-panel" aria-label="Mobile navigation">
      {links.map(([label, href, Icon]) => (
        <a key={href} href={href} className={active === href ? "active" : ""} aria-current={active === href ? "page" : undefined} onClick={() => setActive(href)}>
          <Icon weight={active === href ? "fill" : "regular"} />
          <span>{label}</span>
        </a>
      ))}
    </nav>
  );
}

function OptimizedImage({ imageKey, alt, eager = false }: { imageKey: ImageKey; alt: string; eager?: boolean }) {
  const [loaded, setLoaded] = useState(false);
  const image = APP_CONFIG.images[imageKey];
  const style = { "--focus-desktop": image.desktop, "--focus-mobile": image.mobile } as CSSProperties;
  return (
    <span className={loaded ? "image-wrap loaded" : "image-wrap"} style={style}>
      <span className="image-skeleton" />
      <img src={image.path} alt={alt} loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : "auto"} decoding="async" onLoad={() => setLoaded(true)} />
    </span>
  );
}

function GlassPanel({ className = "", children }: { className?: string; children: ReactNode }) {
  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--spot-x", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--spot-y", `${event.clientY - rect.top}px`);
  };
  return <div className={`glass-panel spotlight ${className}`} onPointerMove={onMove}>{children}</div>;
}

function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reduceMotion = useReducedMotion();
  return <motion.div className={className} initial={reduceMotion ? false : { opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.18 }} transition={{ duration: 0.72, delay, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.div>;
}

export default App;
